import React, { FC } from 'react';
import {
  ClockIcon,
  SunIcon,
  BoltIcon,
  MoonIcon,
} from '@heroicons/react/24/outline';
import SegmentedControl from '@/components/elements/SegmentedControl';
import { Text } from '@/components/atoms/AppText';
import dayjs, { Dayjs } from 'dayjs';
import { ORGANISM_PROPS } from '@/constant/global/global.constant';
import { tid } from '@/utils/testId';
import { DATE_TIME_PICKER_TEST_IDS as TID } from '@/constant/testIds/dateTimePicker.testIds';
import { APP_BG, APP_BORDER } from '@/constant/theme/colors.constant';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { hoursArray, minutesArray } from './constants';
import { TimeDropdown } from './TimeDropdowns';

dayjs.extend(utc);
dayjs.extend(timezone);

interface TimeSelectorProps {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  startHour: string;
  startMinute: string;
  endHour: string;
  endMinute: string;
  onStartHourChange: (hour: string) => void;
  onStartMinuteChange: (minute: string) => void;
  onEndHourChange: (hour: string) => void;
  onEndMinuteChange: (minute: string) => void;
  timezoneDetails: {
    currentTimezone: string;
    timezoneAbbreviation: string;
    currentTime: string;
  };
}

export const TimeSelector: FC<TimeSelectorProps> = ({
  startDate,
  endDate,
  startHour,
  startMinute,
  endHour,
  endMinute,
  onStartHourChange,
  onStartMinuteChange,
  onEndHourChange,
  onEndMinuteChange,
  timezoneDetails,
}) => {
  const now = dayjs().tz(timezoneDetails.currentTimezone);

  const isEndDateToday = endDate?.isSame(now, 'day') ?? false;

  const isDayStartActive = startHour === '00' && startMinute === '00';
  const isDayEndActive = endHour === '23' && endMinute === '59';

  const isNowActive =
    isEndDateToday &&
    endHour === now.format('HH') &&
    endMinute === now.format('mm');

  const handleDayStart = () => {
    onStartHourChange('00');
    onStartMinuteChange('00');
  };

  const handleDayEnd = () => {
    onEndHourChange('23');
    onEndMinuteChange('59');
  };

  const handleNow = () => {
    onEndHourChange(now.format('HH'));
    onEndMinuteChange(now.format('mm'));
  };

  const formatTime = (h: string, m: string) =>
    dayjs().hour(Number(h)).minute(Number(m)).format('hh:mm A');

  return (
    <div
      className={`w-full lg:w-[280px] border-t lg:border-t-0 lg:border-l ${APP_BORDER.default} ${APP_BG.base} px-3 py-3 flex flex-col gap-3`}
    >
      {/* HEADER */}
      <Text
        as="span"
        size="md"
        weight="bold"
        color="muted"
        tracking="wide"
        className="flex items-center gap-2 uppercase"
      >
        <ClockIcon className="w-4 h-4 text-primary" />
        {ORGANISM_PROPS.TIME_SELECTOR.HEADER}
      </Text>

      {/* FROM */}
      <div
        className={`p-2.5 rounded-xl border ${APP_BORDER.default} ${APP_BG.canvas} flex flex-col gap-2`}
      >
        <div className="flex justify-between items-center text-[11px]">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-primary rounded-full" />
            <Text as="span" size="md" weight="semibold" color="muted">
              {ORGANISM_PROPS.TIME_SELECTOR.FROM_LABEL}
            </Text>
          </div>

          <div className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
            <Text as="span" size="md" color="soft">
              {startDate ? startDate.format('MMM DD') : '--'}
            </Text>
            {startDate && (
              <Text
                as="span"
                size="sm"
                weight="semibold"
                color="primary"
                className="px-1.5 py-[1px] rounded bg-primary/10"
              >
                {formatTime(startHour, startMinute)}
              </Text>
            )}
          </div>
        </div>

        <div className="flex justify-between items-center gap-2">
          <div
            className={`flex items-center gap-1 ${APP_BG.base} p-1 rounded-md border ${APP_BORDER.default}`}
          >
            <TimeDropdown
              testId={tid(TID.TIME_START_HOUR)}
              type="hour"
              value={startHour}
              options={hoursArray}
              timezone={timezoneDetails.currentTimezone}
              selectedDate={startDate}
              onChange={onStartHourChange}
            />
            <Text as="span" size="md" color="subtle">
              :
            </Text>
            <TimeDropdown
              testId={tid(TID.TIME_START_MINUTE)}
              type="minute"
              value={startMinute}
              options={minutesArray}
              timezone={timezoneDetails.currentTimezone}
              selectedDate={startDate}
              currentHourSelection={startHour}
              onChange={onStartMinuteChange}
            />
          </div>

          {startDate && (
            <SegmentedControl
              testId={tid(TID.TIME_START_QUICK)}
              size="xs"
              shape="square"
              variant="gradient"
              options={[
                {
                  value: 'start',
                  label: ORGANISM_PROPS.TIME_SELECTOR.START_LABEL,
                  icon: SunIcon,
                },
              ]}
              value={isDayStartActive ? 'start' : ''}
              onChange={handleDayStart}
            />
          )}
        </div>
      </div>

      {/* TO */}
      <div
        className={`p-2.5 rounded-xl border ${APP_BORDER.default} ${APP_BG.canvas} flex flex-col gap-2`}
      >
        <div className="flex justify-between items-center text-[11px]">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 bg-primary rounded-sm" />
            <Text as="span" size="md" weight="semibold" color="muted">
              {ORGANISM_PROPS.TIME_SELECTOR.TO_LABEL}
            </Text>
          </div>

          <div className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
            <Text as="span" size="md" color="soft">
              {endDate ? endDate.format('MMM DD') : '--'}
            </Text>
            {endDate && (
              <Text
                as="span"
                size="sm"
                weight="semibold"
                color="primary"
                className="px-1.5 py-[1px] rounded bg-primary/10"
              >
                {formatTime(endHour, endMinute)}
              </Text>
            )}
          </div>
        </div>

        <div className="flex justify-between items-center gap-2">
          <div
            className={`flex items-center gap-1 ${APP_BG.base} p-1 rounded-md border ${APP_BORDER.default}`}
          >
            <TimeDropdown
              testId={tid(TID.TIME_END_HOUR)}
              type="hour"
              value={endHour}
              options={hoursArray}
              timezone={timezoneDetails.currentTimezone}
              selectedDate={endDate}
              onChange={onEndHourChange}
            />
            <Text as="span" size="md" color="subtle">
              :
            </Text>
            <TimeDropdown
              testId={tid(TID.TIME_END_MINUTE)}
              type="minute"
              value={endMinute}
              options={minutesArray}
              timezone={timezoneDetails.currentTimezone}
              selectedDate={endDate}
              currentHourSelection={endHour}
              onChange={onEndMinuteChange}
            />
          </div>

          {(isEndDateToday || endDate) && (
            <SegmentedControl
              testId={tid(TID.TIME_END_QUICK)}
              size="xs"
              shape="square"
              variant="gradient"
              options={[
                ...(isEndDateToday
                  ? [
                      {
                        value: 'now' as const,
                        label: ORGANISM_PROPS.TIME_SELECTOR.NOW_LABEL,
                        icon: BoltIcon,
                      },
                    ]
                  : []),
                ...(endDate
                  ? [
                      {
                        value: 'end' as const,
                        label: ORGANISM_PROPS.TIME_SELECTOR.END_LABEL,
                        icon: MoonIcon,
                      },
                    ]
                  : []),
              ]}
              value={isNowActive ? 'now' : isDayEndActive ? 'end' : ''}
              onChange={(val) => (val === 'now' ? handleNow() : handleDayEnd())}
            />
          )}
        </div>
      </div>
    </div>
  );
};
